<script setup>
/* Input a chip con autocompletamento e creazione al volo.
 *
 * Rimpiazza `b-taginput` di Buefy, il componente più complesso in uso e uno
 * dei tre che non esistono senza una libreria (con upload e autocomplete).
 * Scritto a mano: ~120 righe contro l'aggiunta di un'intera dipendenza di
 * componenti destinata a invecchiare come Buefy.
 */
import { computed, ref } from 'vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  suggestions: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const draft = ref('');
const focused = ref(false);
const highlighted = ref(-1);

const matches = computed(() => {
  const text = draft.value.trim().toLowerCase();
  if (!text) return [];
  return props.suggestions
    .filter((tag) => tag.toLowerCase().includes(text) && !props.modelValue.includes(tag))
    .slice(0, 8);
});

const showMenu = computed(() => focused.value && matches.value.length > 0);

function add(tag) {
  const clean = String(tag).trim();
  if (!clean || props.modelValue.includes(clean)) {
    draft.value = '';
    return;
  }
  emit('update:modelValue', [...props.modelValue, clean]);
  draft.value = '';
  highlighted.value = -1;
}

function removeAt(index) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
}

function onEnter() {
  if (highlighted.value >= 0 && matches.value[highlighted.value]) {
    add(matches.value[highlighted.value]);
  } else {
    add(draft.value);
  }
}

/** Backspace su campo vuoto toglie l'ultimo chip: comportamento atteso. */
function onBackspace() {
  if (draft.value === '' && props.modelValue.length) {
    removeAt(props.modelValue.length - 1);
  }
}

function move(step) {
  if (!matches.value.length) return;
  const next = highlighted.value + step;
  highlighted.value = (next + matches.value.length) % matches.value.length;
}
</script>

<template>
  <div class="taginput" :class="{ 'is-focused': focused }">
    <ul class="taginput__chips">
      <li v-for="(tag, index) in modelValue" :key="tag" class="chip">
        <span class="chip__text">{{ tag }}</span>
        <button class="chip__x" type="button" :aria-label="`Togli ${tag}`" @click="removeAt(index)">
          <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
            <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" stroke-width="1.6" fill="none" />
          </svg>
        </button>
      </li>
      <li class="taginput__entry">
        <input
          v-model="draft"
          class="taginput__field"
          type="text"
          :placeholder="modelValue.length ? '' : placeholder"
          autocomplete="off"
          role="combobox"
          :aria-expanded="showMenu"
          @focus="focused = true"
          @blur="focused = false"
          @keydown.enter.prevent="onEnter"
          @keydown.,.prevent="onEnter"
          @keydown.backspace="onBackspace"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.esc="focused = false"
        />
      </li>
    </ul>

    <ul v-if="showMenu" class="menu" role="listbox">
      <li v-for="(match, index) in matches" :key="match">
        <!-- mousedown e non click: il blur del campo arriverebbe prima. -->
        <button
          class="menu__item"
          :class="{ 'is-active': index === highlighted }"
          type="button"
          role="option"
          :aria-selected="index === highlighted"
          @mousedown.prevent="add(match)"
        >
          {{ match }}
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.taginput {
  position: relative;
}

.taginput__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0.3rem;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius);
  background: var(--surface);
}

.taginput.is-focused .taginput__chips {
  border-color: var(--accent);
  outline: 2px solid var(--accent-wash);
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px 2px 7px;
  border-radius: 2px;
  background: var(--accent-wash);
  color: var(--accent);
  font-size: var(--t-sm);
}

.chip__x {
  display: grid;
  place-items: center;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.65;
}

.chip__x:hover {
  opacity: 1;
}

.taginput__entry {
  flex: 1;
  min-width: 7rem;
}

.taginput__field {
  width: 100%;
  padding: 0.2rem;
  border: none;
  background: none;
  color: var(--ink);
  font: inherit;
  font-size: var(--t-sm);
}

.taginput__field:focus {
  outline: none;
}

.menu {
  position: absolute;
  z-index: 5;
  top: calc(100% + 3px);
  left: 0;
  right: 0;
  max-height: 12rem;
  overflow-y: auto;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius);
  background: var(--surface-raised);
  box-shadow: var(--shadow-raised);
}

.menu__item {
  display: block;
  width: 100%;
  padding: 0.35rem 0.6rem;
  border: none;
  background: none;
  text-align: left;
  font-size: var(--t-sm);
  cursor: pointer;
}

.menu__item:hover,
.menu__item.is-active {
  background: var(--accent-wash);
  color: var(--accent);
}
</style>
